import React, { useContext, useEffect, useState } from 'react';
import Scrollbar from 'react-scrollbars-custom';
import { Button } from '@blueprintjs/core';
import { VCardPanel, VInputField, VRadioGroupField, VSelectField, VTagInputField } from 'welltrax-ui-components';
import { IItemMultiple } from 'welltrax-ui-components/lib/types/Form/Inputs/SelectMultipleField';
import { observer } from 'mobx-react';

import { useTheme } from '../../../core/hooks/UseTheme';
import { intl } from '../../../core/utils/Intl';
import {
  onlyLettersNumbersSpecialAndNoSpace,
  onlyLowerCaseLettersAnNumbersNoSpace,
  onlyLowerCaseLettersNoSpace
} from '../../../core/utils/utilsForm';
import { NewPortalStore } from '../store/NewPortalStore';
import PasswordInputField from '../../commons/components/PasswordInputField/PasswordInputField';

export interface NewPortalContextInterface {
  newPortalStore: NewPortalStore;
}

export const NewPortalContext = React.createContext<NewPortalContextInterface>({
  newPortalStore: new NewPortalStore()
});

export const NewPortalLayout = observer(() => {
  const theme = useTheme();
  const { newPortalStore } = useContext(NewPortalContext);
  const { form } = newPortalStore;
  const [isTriggeredByUserInput, setIsTriggeredByUserInput] = useState(false);
  newPortalStore.setLoadingFormData(false);

  useEffect(() => {
    const commodityList = form.$.commodityList.value;
    if (!isTriggeredByUserInput) {
      if (form.$.crudeCommodity.value === 'YES') {
        if (commodityList.indexOf('CRUDE') === -1) commodityList.push('CRUDE');
      } else {
        commodityList.splice(commodityList.indexOf('CRUDE'), 1);
      }
    } else {
      if (form.$.crudeCommodity.value === 'NO') {
        commodityList.splice(commodityList.indexOf('CRUDE'), 1);
      } else {
        commodityList.indexOf('CRUDE') === -1 && commodityList.push('CRUDE');
      }
    }

    if (!isTriggeredByUserInput) {
      form.$.commodityList.onChange(commodityList);
    } else {
      setIsTriggeredByUserInput(false);
    }
  }, [form.$.crudeCommodity.value]);

  const onCommodityListChange = (list: Array<String>) => {
    setIsTriggeredByUserInput(true);
    const parsedList = list.map(value => value?.toUpperCase());
    form.$.crudeCommodity.onChange(parsedList.indexOf('CRUDE') === -1 ? 'NO' : 'YES');
    form.$.commodityList.onChange(parsedList);
  };

  return (
    <NewPortalContext.Provider
      value={{
        newPortalStore
      }}
    >
      <VCardPanel
        headerBackgroundColor={theme.headers}
        headerColor={'white'}
        bodyPadding={'0'}
        headerText={intl('newPortal.newPortal', 'New Portal')}
        width={'100%'}
        height={'100%'}
      >
        <Scrollbar height={'100%'}>
          <div style={{ display: 'flex', flexDirection: 'column', padding: '8px', width: '475px' }}>
            <VInputField
              validators={[onlyLowerCaseLettersAnNumbersNoSpace]}
              minimal
              fill
              label={`${intl('newPortal.databaseOrgName', 'Portal Name')}`}
              id={'databaseOrgName'}
              layer={{ labelOrientation: 'start', inputWidth: 12 }}
              fieldState={form.$.databaseOrgName}
              margin={'0 0 5px'}
              required
            />

            <VInputField
              validators={[onlyLowerCaseLettersAnNumbersNoSpace]}
              minimal
              fill
              label={`${intl('newPortal.supportUsername', 'Portal Username')}`}
              id={'supportUsername'}
              layer={{ labelOrientation: 'start', inputWidth: 12 }}
              fieldState={form.$.supportUsername}
              margin={'0 0 5px'}
              required
              autoComplete="off"
            />

            <PasswordInputField
              validators={[onlyLettersNumbersSpecialAndNoSpace]}
              minimal
              fill
              label={`${intl('newPortal.supportPassword', 'Portal Password')}`}
              id={'supportPassword'}
              layer={{ labelOrientation: 'start', inputWidth: 12 }}
              fieldState={form.$.supportPassword}
              margin={'0 0 5px'}
              required
            />

            <VRadioGroupField
              options={[
                { value: 'YES', label: `${intl('app.yes')}` },
                { value: 'NO', label: `${intl('app.no')}` }
              ]}
              id={'financeModule'}
              label={`${intl('newPortal.financeModule')}:`}
              fieldState={form.$.financeModule}
              inline
              fill
              layer={{
                labelWidth: 4,
                labelOrientation: 'start'
              }}
              margin={'0'}
            />

            <VRadioGroupField
              options={[
                { value: 'YES', label: `${intl('app.yes')}` },
                { value: 'NO', label: `${intl('app.no')}` }
              ]}
              id={'complianceModule'}
              label={`${intl('newPortal.complianceModule')}:`}
              fieldState={form.$.complianceModule}
              inline
              fill
              layer={{
                labelWidth: 4,
                labelOrientation: 'start'
              }}
              margin={'0'}
            />

            <VRadioGroupField
              options={[
                { value: 'YES', label: `${intl('app.yes')}` },
                { value: 'NO', label: `${intl('app.no')}` }
              ]}
              id={'crudeCommodity'}
              label={`${intl('newPortal.crudeCommodity')}:`}
              fieldState={form.$.crudeCommodity}
              inline
              fill
              layer={{
                labelWidth: 4,
                labelOrientation: 'start'
              }}
              margin={'0'}
            />

            <VTagInputField
              fill
              tooltip="if crude commodity, it must be typed CRUDE"
              label={`${intl('newPortal.commodityName', 'Commodity Name')}`}
              id={'commodityName'}
              layer={{ labelOrientation: 'start', inputWidth: 12 }}
              fieldState={form.$.commodityList}
              margin={'0 0 5px'}
              onChange={onCommodityListChange}
              required={form.$.crudeCommodity.value === 'NO'}
            />

            <VSelectField
              allowOrder
              fill
              clearButton
              popoverProps={{ minimal: true }}
              label={`${intl('newPortal.eld', 'ELD')}`}
              id={'eld'}
              options={ELDOptions}
              layer={{ labelOrientation: 'start', inputWidth: 12 }}
              fieldState={form.$.eld}
              margin={'0 0 5px'}
            />

            {form.$.eld.value === 'GEOTAB' && (
              <div>
                <VInputField
                  validators={[onlyLowerCaseLettersAnNumbersNoSpace]}
                  minimal
                  fill
                  label={`${intl('newPortal.geotabUsername', 'Geotab Username')}`}
                  id={'geotabUsername'}
                  layer={{ labelOrientation: 'start', inputWidth: 12 }}
                  fieldState={form.$.geotabUsername}
                  margin={'0 0 5px'}
                  required={form.$.geotabPassword.value || form.$.eld.value === 'GEOTAB'}
                />

                <PasswordInputField
                  validators={[onlyLettersNumbersSpecialAndNoSpace]}
                  minimal
                  fill
                  label={`${intl('newPortal.geotabPassword', 'Geotab Password')}`}
                  id={'geotabPassword'}
                  layer={{ labelOrientation: 'start', inputWidth: 12 }}
                  fieldState={form.$.geotabPassword}
                  margin={'0 0 5px'}
                  required={form.$.geotabUsername.value || form.$.eld.value === 'GEOTAB'}
                />

                <VInputField
                  validators={[onlyLowerCaseLettersNoSpace()]}
                  minimal
                  fill
                  label={`${intl('newPortal.geotabDatabaseName', 'Geotab  Database Name')}`}
                  id={'geotabDatabaseName'}
                  layer={{ labelOrientation: 'start', inputWidth: 12 }}
                  fieldState={form.$.geotabDatabaseName}
                  margin={'0 0 5px'}
                  required={form.$.eld.value === 'GEOTAB'}
                />
              </div>
            )}

            {form.$.eld.value === 'SAMSARA' && (
              <VInputField
                validators={[onlyLettersNumbersSpecialAndNoSpace]}
                minimal
                fill
                label={`${intl('newPortal.samsaraToken', 'Samsara Token')}`}
                id={'samsaraToken'}
                layer={{ labelOrientation: 'start', inputWidth: 12 }}
                fieldState={form.$.samsaraToken}
                margin={'0 0 5px'}
                required={form.$.eld.value === 'SAMSARA'}
              />
            )}
            <VInputField
              validators={[onlyLowerCaseLettersAnNumbersNoSpace]}
              minimal
              fill
              label={`${intl('newPortal.vapiUsername', 'VAPI Username')}`}
              id={'vapiUsername'}
              layer={{ labelOrientation: 'start', inputWidth: 12 }}
              fieldState={form.$.vapiUsername}
              margin={'0 0 5px'}
              required={form.$.vapiPassword.value}
            />
            <PasswordInputField
              validators={[onlyLettersNumbersSpecialAndNoSpace]}
              minimal
              fill
              label={`${intl('newPortal.vapiPassword', 'VAPI Password')}`}
              id={'vapiPassword'}
              layer={{ labelOrientation: 'start', inputWidth: 12 }}
              fieldState={form.$.vapiPassword}
              margin={'0 0 5px'}
              required={form.$.vapiUsername.value}
            />
            <VInputField
              validators={[onlyLowerCaseLettersAnNumbersNoSpace]}
              minimal
              fill
              label={`${intl('newPortal.ticketAPIUsername', 'Ticket API Username')}`}
              id={'ticketApiUsername'}
              layer={{ labelOrientation: 'start', inputWidth: 12 }}
              fieldState={form.$.ticketApiUsername}
              margin={'0 0 5px'}
              required={form.$.ticketApiPassword.value}
            />
            <PasswordInputField
              validators={[onlyLettersNumbersSpecialAndNoSpace]}
              minimal
              fill
              label={`${intl('newPortal.ticketAPIPassword', 'Ticket API Password')}`}
              id={'ticketApiPassword'}
              layer={{ labelOrientation: 'start', inputWidth: 12 }}
              fieldState={form.$.ticketApiPassword}
              margin={'0 0 5px'}
              required={form.$.ticketApiUsername.value}
            />
            <Button
              text={`${intl('newPortal.createPortal', 'Create Portal')}`}
              icon={'tick'}
              onClick={() => newPortalStore.handleSave()}
              minimal
            />
          </div>
        </Scrollbar>
      </VCardPanel>
    </NewPortalContext.Provider>
  );
});

export default NewPortalLayout;

const ELDOptions: IItemMultiple[] = [
  { value: 'SAMSARA', label: 'SAMSARA' },
  { value: 'GEOTAB', label: 'GEOTAB' },
  { value: 'NONE', label: 'NONE' }
];
